import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyTheme, cacheTheme, themeValues } from "@/lib/theme";

const Ctx = createContext(null);

export const AppearanceProvider = ({ children }) => {
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (user) api.get("/appearance").then((r) => setApp(r.data)).catch(() => {});
    else if (user === false) { setApp(null); setPreview(null); cacheTheme(null); applyTheme(null); }
  }, [user?.id, user?.is_pro]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = app ? themeValues(app.theme_id, app.themes) : null;
  useEffect(() => {
    if (preview) return applyTheme(preview);
    if (!app) return;
    applyTheme(active);
    cacheTheme(app.theme_id === "dark" ? null : active);
  }, [app, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const put = useCallback(async (body) => setApp((await api.put("/appearance", body)).data), []);
  const saveTheme = async (id, name, values) => {
    const { data } = id ? await api.put(`/appearance/themes/${id}`, { name, values }) : await api.post("/appearance/themes", { name, values });
    setApp((a) => ({ ...a, themes: id ? a.themes.map((t) => (t.id === id ? data : t)) : [...a.themes, data] }));
    return data;
  };
  const deleteTheme = async (id) => {
    await api.delete(`/appearance/themes/${id}`);
    setApp((a) => ({ ...a, themes: a.themes.filter((t) => t.id !== id), theme_id: a.theme_id === id ? "dark" : a.theme_id }));
  };
  const design = (id) => app?.scene_breaks.find((b) => b.id === id) || app?.scene_breaks.find((b) => b.id === "default");

  return (
    <Ctx.Provider value={{ app, active, setTheme: (theme_id) => put({ theme_id }), setSceneBreak: (scene_break) => put({ scene_break }),
      saveTheme, deleteTheme, setPreview, design }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAppearance = () => useContext(Ctx);
